import {loadScript} from "../utils/load_script";
import mapConfig from "../config/map.config";
import he from 'he';
import travelModeStyles from 'bundle-text:../styles/components/travel_modes.scss';

export default class WoosmapMapElement extends HTMLElement {
    public map!: woosmap.map.Map;
    public infoWindow!: woosmap.map.InfoWindow;
    private shadow: ShadowRoot;
    private markers: { name: string, coordinates: { lat: number, lng: number } }[] = [];
    private searchLocation?: woosmap.map.LatLngLiteral;
    private route: { mode?: string } = {mode: 'DRIVING'};
    private transitRenderer?: woosmap.map.TransitRenderer;
    private directionsRenderer?: woosmap.map.DirectionsRenderer;

    constructor() {
        super();
        this.shadow = this.attachShadow({mode: 'open'});
        this.style.height = '100%';
        this.style.width = '100%';
    }

    static get observedAttributes() {
        return ['markers', 'search-location', 'route'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        try {
            if (name === 'markers' && newValue && he.decode(newValue, true)) {
                const decodedValue = he.decode(newValue);
                this.markers = JSON.parse(decodedValue);
                if (this.map) {
                    this.updateMarkersAndFitBounds();
                }
            } else if (name === 'search-location' && newValue) {
                const decodedValue = he.decode(newValue);
                this.searchLocation = JSON.parse(decodedValue);
                if (this.map) {
                    this.updateMarkersAndFitBounds();
                }
            } else if (name === 'route' && newValue) {
                const decodedValue = he.decode(newValue);
                this.route = JSON.parse(decodedValue);
            }
        } catch (error) {
            console.log('Error parsing JSON:', error, 'Input:', newValue);
        }
    }

    connectedCallback() {
        this.render();
    }

    render() {
        const mapId = this.getAttribute('id') || 'map';
        this.shadow.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
        }
        .map-container {
          width: 100%;
          height: 100%;
        }
      </style>
      <style>${travelModeStyles}</style>
      <div id="${mapId}" class="map-container"></div>
    `;
        loadScript({
            url: "https://sdk.woosmap.com/map/map.js",
            params: {key: "woos-48c80350-88aa-333e-835a-07f4b658a9a4", language: "en"},
            override: true
        })
            .then(() => {
                this.copyStylesToShadowRoot();
                this.initMapView(mapId);
            })
            .catch((error) => {
                console.error("Failed to load Woosmap Map JS SDK script", error);
            });
    }

    copyStylesToShadowRoot() {
        const styles = Array.from(document.head.querySelectorAll('style[type="text/css"]'));
        styles.forEach(style => {
            if (style.textContent && style.textContent.includes('.mapboxgl-map')) {
                this.shadow.appendChild(style.cloneNode(true));
            }
        });
    }

    initMapView(mapId: string) {
        const mapContainer = this.shadow.getElementById(mapId);
        if (mapContainer) {
            this.map = new woosmap.map.Map(mapContainer, mapConfig.mapOptions);
            this.infoWindow = new woosmap.map.InfoWindow({});
            this.infoWindow.setOffset(new woosmap.map.Point(0, -40));
            this.updateMarkersAndFitBounds();
            this.map.addListener('click', () => {
                this.infoWindow.close();
            })
            if (this.route?.mode && this.markers.length == 1) {
                this.showDirections(this.markers[0].coordinates);
            }
        } else {
            console.error("Map container not found in shadow DOM");
        }
    }

    updateMarkersAndFitBounds() {
        const bounds = new woosmap.map.LatLngBounds();
        if (this.markers && this.markers.length > 0) {
            this.addStoreMarkers(this.markers, bounds);
        }
        if (this.searchLocation) {
            this.addSearchLocation(this.searchLocation, bounds);
        }
        if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, mapConfig.mapPaddings.full);
        }
        if (this.markers && this.markers.length == 1 && !this.searchLocation) {
            this.map.setCenter({lat: this.markers[0].coordinates.lat, lng: this.markers[0].coordinates.lng});
            this.map.setZoom(12);
        }
    }

    addSearchLocation(location: woosmap.map.LatLngLiteral, bounds: woosmap.map.LatLngBounds) {
        this.addMarker("", location, mapConfig.userLocationIconOptions, bounds);
    }

    addStoreMarkers(markers: {
        name: string,
        coordinates: { lat: number, lng: number }
    }[], bounds: woosmap.map.LatLngBounds) {
        const iconUrls = [
            'https://images.woosmap.com/starbucks-marker-1.svg',
            'https://images.woosmap.com/starbucks-marker-2.svg',
            'https://images.woosmap.com/starbucks-marker-3.svg',
            'https://images.woosmap.com/starbucks-marker-4.svg',
            'https://images.woosmap.com/starbucks-marker-5.svg',
            'https://images.woosmap.com/starbucks-marker-6.svg',
            'https://images.woosmap.com/starbucks-marker-7.svg',
            'https://images.woosmap.com/starbucks-marker-8.svg',
            'https://images.woosmap.com/starbucks-marker-9.svg',
            'https://images.woosmap.com/starbucks-marker-10.svg'
        ];

        markers.forEach((marker, index) => {
            let iconOptions = mapConfig.storeIconOptions;
            if (index < iconUrls.length && this.markers.length > 1) {
                iconOptions = {
                    url: iconUrls[index],
                    scaledSize: new woosmap.map.Size(30, 30)
                };
            }
            this.addMarker(marker.name, marker.coordinates, iconOptions, bounds);
        });
    }

    addMarker(name: string, position: woosmap.map.LatLngLiteral, iconOptions: any, bounds: woosmap.map.LatLngBounds) {
        const markerInstance = new woosmap.map.Marker({
            position: new woosmap.map.LatLng(position.lat, position.lng),
            icon: iconOptions
        });
        markerInstance.setMap(this.map);
        bounds.extend(markerInstance.getPosition());
        if (name) {
            markerInstance.addListener("click", () => {
                const position = markerInstance.getPosition()
                this.infoWindow.setContent(`<div style="width:160px"><h3 style="color:#1D1D1D; margin-top:5px">${name}</h3>${this.getTravelModeContainer()}</div>`);
                this.infoWindow.open(this.map, position);
                this.showDirections(position);
                this.addTravelModeListeners(position);
            });
        }
    }

    showDirections(destination: woosmap.map.LatLng | woosmap.map.LatLngLiteral) {
        if (this.route && this.route.mode && this.searchLocation && destination) {
            if (this.transitRenderer) {
                this.transitRenderer.setMap(null);
            }
            if (this.directionsRenderer){
                this.directionsRenderer.setMap(null);
            }
            if (this.route.mode.toLowerCase() === 'transit') {
                const transitService = new woosmap.map.TransitService();
                this.transitRenderer = new woosmap.map.TransitRenderer({preserveViewport: true});
                this.transitRenderer.setMap(this.map);

                transitService.route({
                    origin: this.searchLocation,
                    destination: destination
                }).then((response) => {
                    if (this.transitRenderer) {
                        this.transitRenderer.setRoutes(response.routes);
                    }
                }).catch((error) => {
                    console.error("Transit directions request failed:", error);
                });
            }
            else {
                const directionsService = new woosmap.map.DirectionsService();
                this.directionsRenderer = new woosmap.map.DirectionsRenderer({preserveViewport: true});
                this.directionsRenderer.setMap(this.map);

                const request = {
                    origin: this.searchLocation,
                    destination: destination,
                    travelMode: this.route.mode
                };

                directionsService.route(request, (result, status) => {
                    if (status === 'OK') {
                        if (this.directionsRenderer){
                            this.directionsRenderer.setDirections(result);
                        }
                    } else {
                        console.error('Directions request failed due to ' + status);
                    }
                });
            }
        }
    }

    getTravelModeContainer() {
        return `<div id="travelModeContainer">
                    <div class="travelModeSelector">
                        <div data-mode="DRIVING" class="travelMode">
                            <button>
                                <img alt="DRIVING" class="iconTravelMode iconTravelMode__DRIVING"
                                     src="https://images.woosmap.com/directions/drive_black.png">
                            </button>
                        </div>
                        <div data-mode="WALKING" class="travelMode">
                            <button>
                                <img alt="WALKING" class="iconTravelMode iconTravelMode__WALKING"
                                     src="https://images.woosmap.com/directions/walk_black.png">
                            </button>
                        </div>
                        <div data-mode="CYCLING" class="travelMode">
                            <button>
                                <img alt="CYCLING" class="iconTravelMode iconTravelMode__CYCLING"
                                     src="https://images.woosmap.com/directions/bicycle_black.png">
                            </button>
                        </div>
                        <div data-mode="TRANSIT" class="travelMode">
                            <button>
                                <img alt="TRANSIT" class="iconTravelMode iconTravelMode__TRANSIT"
                                    src="https://images.woosmap.com/directions/transit_black.png">
                            </button>
                        </div>
                    </div>
                </div>`;
    }

    addTravelModeListeners(destination: woosmap.map.LatLng) {
        const travelModeButtons = this.shadow.querySelectorAll('.travelMode button');
        travelModeButtons.forEach(button => {
            button.addEventListener('click', (event) => {
                const mode = (event.currentTarget as HTMLElement).parentElement?.getAttribute('data-mode');
                if (mode) {
                    this.route.mode = mode;
                    this.showDirections(destination);
                }
            });
        });
    }
}